import {
  MILESTONE_DAYS,
  type MilestoneDay,
  type OnboardingResource,
  type OnboardingTemplate,
} from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { CanvasMilestonesPanel } from "@/components/canvas/canvas-milestones-panel";
import { useTemplateMilestonesDataSource } from "@/components/canvas/use-template-data-source";
import { WelcomeMessageEditor } from "@/components/canvas/welcome-message-editor";
import { DeleteTemplateDialog } from "@/components/onboarding-templates/delete-template-dialog";
import { PreviewPortalProvider } from "@/components/portal/preview-context";
import { ResourcePreviewPane } from "@/components/preview/resource-preview-pane";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";
import { PortalJourney } from "@/pages/portal/portal-journey";

export default function TemplateCanvasPage() {
  const [, params] = useRoute<{ id: string }>("/onboarding-templates/:id/canvas");
  const [, navigate] = useLocation();
  const id = params?.id ?? null;

  const { employee, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [isEditing, setIsEditing] = useState(true);
  const [previewSelectedDay, setPreviewSelectedDay] = useState<number>(MILESTONE_DAYS[0]);
  const [selectedResource, setSelectedResource] = useState<OnboardingResource | null>(null);
  const [previewResourceId, setPreviewResourceId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const tplQueryOptions = orpc.onboardingTemplates.getById.queryOptions({
    input: { id: id ?? "" },
  });
  const tplQuery = useQuery({ ...tplQueryOptions, enabled: !!id });
  const tpl = (tplQuery.data as OnboardingTemplate | undefined) ?? null;

  const isOwner = isSuperAdmin || (!!employee && !!tpl && employee.id === tpl.ownerId);

  const milestonesDataSource = useTemplateMilestonesDataSource(id ?? "", { isOwner });

  const previewQuery = useQuery({
    ...orpc.preview.getTemplatePreview.queryOptions({
      input: { templateId: id ?? "", dayNumber: previewSelectedDay },
    }),
    enabled: !!id && !isEditing,
  });

  const updateNameMut = useMutation({
    mutationFn: (input: {
      id: string;
      name: string;
      sidebarBlockLabel: string;
      ownerId: string;
      welcomeMessages: Record<string, string>;
    }) => rpcClient.onboardingTemplates.update(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      if (tpl) {
        await queryClient.invalidateQueries({
          queryKey: orpc.onboardingTemplates.getById.queryOptions({ input: { id: tpl.id } })
            .queryKey,
        });
      }
      toast({ title: "Nome atualizado" });
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message ?? String(err);
      toast({
        title: "Não foi possível atualizar o nome",
        description: message,
        variant: "destructive",
      });
    },
  });

  const updateWelcomeMut = useMutation({
    mutationFn: (welcomeMessages: Record<string, string>) => {
      if (!tpl) throw new Error("Template não carregado");
      return rpcClient.onboardingTemplates.update({
        id: tpl.id,
        name: tpl.name,
        sidebarBlockLabel: tpl.sidebarBlockLabel,
        ownerId: tpl.ownerId,
        welcomeMessages,
      });
    },
    onSuccess: async () => {
      if (tpl) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: orpc.onboardingTemplates.getById.queryOptions({ input: { id: tpl.id } })
              .queryKey,
          }),
          queryClient.invalidateQueries({
            queryKey: orpc.preview.getTemplatePreview.queryOptions().queryKey,
          }),
        ]);
      }
    },
    onError: (err: unknown) => {
      toast({
        title: "Não foi possível salvar a mensagem do marco",
        description: (err as { message?: string })?.message ?? String(err),
        variant: "destructive",
      });
    },
  });

  const publishMut = useMutation({
    mutationFn: (templateId: string) => rpcClient.onboardingTemplates.publish({ templateId }),
    onSuccess: async () => {
      if (tpl) {
        await queryClient.invalidateQueries({
          queryKey: orpc.onboardingTemplates.getById.queryOptions({ input: { id: tpl.id } })
            .queryKey,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      toast({ title: "Template publicado" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Não foi possível publicar o template",
        description: (err as { message?: string })?.message ?? String(err),
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (isEditingName && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [isEditingName]);

  if (!id) {
    return <div className="p-6 text-sm text-destructive">ID do template ausente na URL.</div>;
  }

  if (tplQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  }

  if (tplQuery.isError || !tpl) {
    return <div className="p-6 text-sm text-destructive">Template não encontrado.</div>;
  }

  function startEditing() {
    if (!isOwner || !tpl) return;
    setDraftName(tpl.name);
    setIsEditingName(true);
  }

  function commitEditing() {
    if (!tpl) return;
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === tpl.name) {
      setIsEditingName(false);
      return;
    }
    updateNameMut.mutate({
      id: tpl.id,
      name: trimmed,
      sidebarBlockLabel: tpl.sidebarBlockLabel,
      ownerId: tpl.ownerId,
      welcomeMessages: tpl.welcomeMessages ?? {},
    });
    setIsEditingName(false);
  }

  function cancelEditing() {
    setIsEditingName(false);
  }

  return (
    <div className="flex flex-col -m-6 flex-1 min-h-0">
      <div className="bg-background border-b border-border flex items-center gap-3 px-7 h-14">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            // biome-ignore lint/security/noSecrets: URL path, not a secret
            onClick={() => navigate("/onboardings?tab=templates")}
            data-testid="canvas-back-btn"
          >
            ← Templates
          </Button>
          <span className="text-border select-none">/</span>
          <div data-testid="canvas-breadcrumb">
            {isEditingName ? (
              <input
                ref={inputRef}
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={commitEditing}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitEditing();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEditing();
                  }
                }}
                maxLength={60}
                className="text-lg font-semibold tracking-tight text-foreground bg-transparent border-b border-border outline-none focus:border-foreground px-0.5 min-w-[8rem]"
                data-testid="canvas-name-input"
              />
            ) : isOwner ? (
              <button
                type="button"
                onClick={startEditing}
                className="group inline-flex items-center gap-1 cursor-pointer text-lg font-semibold tracking-tight text-foreground transition-colors"
                data-testid="canvas-name-edit-trigger"
              >
                <span>{tpl.name}</span>
                <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ) : (
              <span className="text-lg font-semibold tracking-tight text-foreground">
                {tpl.name}
              </span>
            )}
          </div>
          <span
            className={
              tpl.state === "published"
                ? "text-[13px] font-semibold text-[hsl(var(--forest-pulse))]"
                : "text-[13px] font-semibold text-[hsl(var(--ink-muted))]"
            }
            data-testid="canvas-status-text"
          >
            {tpl.state === "published" ? "Publicado" : "Rascunho"}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="template-edit-toggle"
              checked={isEditing}
              onCheckedChange={(checked) => {
                setSelectedResource(null);
                setPreviewResourceId(null);
                setIsEditing(checked);
              }}
              data-testid="canvas-preview-btn"
            />
            <Label htmlFor="template-edit-toggle" className="cursor-pointer text-sm font-medium">
              Modo edição
            </Label>
          </div>
          {isOwner && tpl.state === "draft" && (
            <Button
              size="sm"
              onClick={() => publishMut.mutate(tpl.id)}
              disabled={publishMut.isPending}
              data-testid="canvas-publish-btn"
            >
              {publishMut.isPending ? "Publicando..." : "Publicar"}
            </Button>
          )}
          {isOwner && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  aria-label="Mais ações"
                  data-testid="canvas-overflow-btn"
                >
                  ···
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  className="w-full text-left text-sm rounded px-3 py-2 text-destructive hover:bg-destructive/5 transition-colors"
                  data-testid="canvas-delete-btn"
                >
                  Excluir template
                </button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {isEditing ? (
            selectedResource ? (
              <main className="flex-1 overflow-y-auto px-6 py-8">
                <ResourcePreviewPane
                  templateId={tpl.id}
                  resourceId={selectedResource.id}
                  onBack={() => setSelectedResource(null)}
                />
              </main>
            ) : (
              <CanvasMilestonesPanel
                dataSource={milestonesDataSource}
                isOwner={isOwner}
                onResourceClick={setSelectedResource}
                renderWelcomeForDay={(day: MilestoneDay) => {
                  const current = tpl.welcomeMessages ?? {};
                  return (
                    <WelcomeMessageEditor
                      milestoneDay={day}
                      value={current[String(day)] ?? ""}
                      isOwner={isOwner}
                      onCommit={(next) => {
                        const merged = { ...current };
                        if (next === "") delete merged[String(day)];
                        else merged[String(day)] = next;
                        updateWelcomeMut.mutate(merged);
                      }}
                    />
                  );
                }}
              />
            )
          ) : previewResourceId ? (
            <main className="flex-1 overflow-y-auto px-6 py-8">
              <ResourcePreviewPane
                templateId={tpl.id}
                resourceId={previewResourceId}
                onBack={() => setPreviewResourceId(null)}
              />
            </main>
          ) : previewQuery.isLoading || !previewQuery.data ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <PreviewPortalProvider
              value={{
                templateId: previewQuery.data.templateId,
                availableDays: previewQuery.data.availableDays,
                today: previewQuery.data.today,
                milestone: previewQuery.data.milestone,
                sidebar: previewQuery.data.sidebar,
                milestoneSelected: previewSelectedDay,
                setMilestoneSelected: (day) => {
                  setPreviewResourceId(null);
                  setPreviewSelectedDay(day);
                },
                onResourceNavigate: setPreviewResourceId,
              }}
            >
              <main className="flex-1 overflow-y-auto px-6 py-8">
                <PortalJourney />
              </main>
            </PreviewPortalProvider>
          )}
        </div>
      </div>

      <DeleteTemplateDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        template={{ id: tpl.id, name: tpl.name }}
        // biome-ignore lint/security/noSecrets: URL path, not a secret
        onDeleted={() => navigate("/onboardings?tab=templates")}
      />
    </div>
  );
}
